export { circleToPolygon } from "./circleToPolygon";
import { circleToPolygon } from "./circleToPolygon";
import { stripPenetrationStylingForPDF } from './highlightPenetrationPhrases';

/**
 * Builds summary rows from analysis results, optimized for performance with indexed matching.
 * 
 * @param {Array} analysis - Array of analysis results, each containing properties and matches
 * @param {string} mode - Output mode, either "html" or "pdf" (affects output formatting)
 * @param {Function|null} t - Translation function for internationalization
 * @param {Object|null} omniAnalysis - OMNI departure analysis results (optional)
 * @returns {Array} Array of summary rows, each row containing 7 elements:
 *   [objektnamn, typ, inom, !inom, kommentar, utanAnm/anmarkning based on mode]
 * 
 * Performance: Uses indexed matching to reduce repeated filtering operations.
 * External API: Maintains identical function signature and output format.
 */
export function buildSummaryRowsFromAnalysis(analysis, mode = "html", t = null, omniAnalysis = null, oasAnalysis = null, baroVnavAnalysis = null, vssAnalysis = null, shieldingAnalysis = null, holdingBaseAnalysis = null, holdingRacetrackAnalysis = null, rnavHoldingAnalysis = null, rnpHoldingAnalysis = null, msaAnalysis = null, taaAnalysis = null, circlingAnalysis = null, gnssApchAnalysis = null, locAnalysis = null) {
  // Kopiera logik från ResultatTable.jsx useEffect/rowbyggare!
  // Define analysis types with translation support
  const getAnalysisTypes = (t) => [
    {
      typ: t ? t("analysisType_surfacesAnnex14") : "Hinderbegränsande ytor (ANNEX 14)",
      regulation: "ANNEX 14",
      matchFn: m => (m.regulation || "").toUpperCase() === "ANNEX 14",
    },
    {
      typ: t ? t("analysisType_surfacesEasa") : "Hinderbegränsande ytor (EASA)",
      regulation: "EASA",
      matchFn: m => (m.regulation || "").toUpperCase() === "EASA",
    },
    {
      typ: t ? t("analysisType_surfacesEtod") : "Hinderbegränsande ytor (eTOD)",
      regulation: "etod",
      matchFn: m => (m.regulation || "").toUpperCase() === "ETOD",
    },
    {
      typ: t ? t("analysisType_airspaceTmaTia") : "Luftrum (TMA/TIA)",
      regulation: "TMA_TIA",
      matchFn: m =>
        typeof m.airspace_type === "string" &&
        (m.airspace_type.toUpperCase().includes("TMA") || m.airspace_type.toUpperCase().includes("TIA")),
    },
    {
      typ: t ? t("analysisType_airspaceAts") : "Luftrum (CTR/TIZ/RMZ/ATS)",
      regulation: "ATS",
      matchFn: m =>
        typeof m.airspace_type === "string" &&
        (
          m.airspace_type.toUpperCase().includes("ATS") ||
          m.airspace_type.toUpperCase().includes("CTR") ||
          m.airspace_type.toUpperCase().includes("TIZ") ||
          m.airspace_type.toUpperCase().includes("RMZ")
        ),
    },
    {
      typ: t ? t("analysisType_proceduresPansOps") : "Flygprocedurer (PANS OPS)",
      regulation: "PANS OPS",
      matchFn: m => (m.regulation || "").toUpperCase() === "PANS OPS",
    },
    {
      typ: t ? t("analysisType_technicalIcao") : "Teknisk utrustning (ICAO Doc 015)",
      regulation: "ICAO Doc 015",
      matchFn: m => (m.regulation || "").toUpperCase() === "ICAO DOC 015",
    },
  ];

  const ANALYS_DEFS = getAnalysisTypes(t);

  /**
   * Extracts a meaningful object name from properties with fallback logic.
   * 
   * @param {Object} properties - Feature properties object  
   * @param {number} idx - Index for fallback naming
   * @returns {string} Object name for display
   */
  function getObjektnamn(properties, idx) {
    return (
      properties?.circleName ||
      properties?.namn ||
      properties?.name ||
      properties?.title ||
      properties?.id ||
      (properties?.circleId !== undefined ? `CIRKEL #${properties.circleId}` : `Objekt #${idx + 1}`)
    );
  }

  /**
   * Builds OMNI obstacle comments for PANS OPS regulation
   * Shows obstacles individually with their own impact status
   * Filters to show only obstacles matching the specified name (for summary table rows)
   * @param {Object|null} omniAnalysis - OMNI analysis results
   * @param {Function|null} t - Translation function
   * @param {string|null} obstacleNameFilter - Optional obstacle name to filter by (for summary table)
   * @returns {Array} Array of formatted OMNI comment strings
   */
  function buildOmniComments(omniAnalysis, t, obstacleNameFilter = null) {
    if (!omniAnalysis || !omniAnalysis.results || omniAnalysis.results.length === 0) {
      return [];
    }

    // Deduplicate by (airportName, runway, areaType) taking worst-case impact.
    // This prevents duplicate comment lines when a line/polygon is drawn and its
    // many interpolated points all share the same obstacle name.
    const grouped = new Map();

    for (const result of omniAnalysis.results) {
      if (!result.obstacles || result.obstacles.length === 0) continue;

      const airportName = result.input.airport_name || result.input.airport_code;
      const runway = result.input.runway_designation;

      for (const obstacle of result.obstacles) {
        if (obstacleNameFilter && obstacle.obstacleName !== obstacleNameFilter) {
          continue;
        }

        const key = `${airportName}||${runway}||${obstacle.areaType}`;
        const existing = grouped.get(key);
        if (!existing) {
          grouped.set(key, {
            airportName,
            runway,
            areaType: obstacle.areaType,
            isCritical: !!obstacle.isCritical,
            isCloseIn: !!obstacle.isCloseIn,
          });
        } else {
          // Take worst-case: any critical point → critical; any non-close-in → not all close-in
          if (obstacle.isCritical) existing.isCritical = true;
          if (!obstacle.isCloseIn) existing.isCloseIn = false;
        }
      }
    }

    const comments = [];
    for (const entry of grouped.values()) {
      let impactStatus;
      let needsImpactHighlight = false;

      if (entry.isCloseIn && !entry.isCritical) {
        const noImpactText = t ? t("omniNoImpactCio") : "no impact";
        const cioLabel = t ? ` (${t("omniCio")})` : " (CIO)";
        impactStatus = cioLabel + ", " + `~~NOIMPACT~~${noImpactText}~~NOIMPACTEND~~`;
      } else if (entry.isCritical) {
        impactStatus = t ? t("omniImpact") : "impact";
        needsImpactHighlight = true;
      } else {
        const noImpactText = t ? t("omniNoImpact") : "no impact";
        impactStatus = `~~NOIMPACT~~${noImpactText}~~NOIMPACTEND~~`;
      }

      if (needsImpactHighlight) {
        impactStatus = `~~IMPACT~~${impactStatus}~~IMPACTEND~~`;
      }

      const comment = [
        entry.airportName,
        `RWY ${entry.runway}`,
        t ? t("omniDepartures") : "OMNI Departures",
        `${t ? t("omniObjectWithin") : "Object within"} ${entry.areaType}`,
        impactStatus
      ].join(", ");

      comments.push(comment);
    }

    return comments;
  }

  /**
   * Builds OAS obstacle comments for PANS OPS regulation.
   * Shows one entry per (airport, runway, OAS category, aircraft category) with worst-case impact.
   * @param {Object|null} oasAnalysis - OAS analysis results
   * @param {Function|null} t - Translation function
   * @param {string|null} obstacleNameFilter - Optional obstacle name to filter by
   * @returns {Array} Array of formatted OAS comment strings
   */
  function buildOasComments(oasAnalysis, t, obstacleNameFilter = null) {
    if (!oasAnalysis || !oasAnalysis.results || oasAnalysis.results.length === 0) {
      return [];
    }

    // Group by (airportName, runway, oasCategory) → worst-case isCritical across all aircraft categories
    const grouped = new Map();

    for (const result of oasAnalysis.results) {
      if (!result.categoryResults) continue;

      const airportName = result.input.airport_name || result.input.airport_code;
      const runway = result.input.runway_designation;
      const oasCategory = result.input.oas_category;

      for (const catRes of result.categoryResults) {
        if (!catRes.obstacles || catRes.obstacles.length === 0) continue;

        const matchingObs = obstacleNameFilter
          ? catRes.obstacles.filter(o => o.obstacleName === obstacleNameFilter)
          : catRes.obstacles;

        if (matchingObs.length === 0) continue;

        const key = `${airportName}||${runway}||${oasCategory}`;
        const hasCritical = matchingObs.some(o => o.isCritical);
        grouped.set(key, {
          airportName, runway, oasCategory,
          isCritical: (grouped.get(key)?.isCritical || hasCritical),
        });
      }
    }

    const comments = [];
    for (const entry of grouped.values()) {
      let impactStatus = entry.isCritical
        ? (t ? t("omniImpact") : "impact")
        : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
      if (entry.isCritical) {
        impactStatus = `~~IMPACT~~${impactStatus}~~IMPACTEND~~`;
      }
      comments.push([
        entry.airportName,
        `RWY ${entry.runway}`,
        `OAS (${entry.oasCategory})`,
        impactStatus,
      ].join(', '));
    }

    return comments;
  }

  /**
   * Builds BARO VNAV obstacle comments for PANS OPS regulation.
   * Shows one entry per (airport, runway, aircraft category) with worst-case impact.
   * @param {Object|null} baroVnavAnalysis - BARO VNAV analysis results
   * @param {Function|null} t - Translation function
   * @param {string|null} obstacleNameFilter - Optional obstacle name to filter by
   * @returns {Array} Array of formatted BARO VNAV comment strings
   */
  function buildBaroVnavComments(baroVnavAnalysis, t, obstacleNameFilter = null) {
    if (!baroVnavAnalysis || !baroVnavAnalysis.results || baroVnavAnalysis.results.length === 0) {
      return [];
    }

    // Group by (airportName, runway) → worst-case isCritical across all aircraft categories
    const grouped = new Map();

    for (const result of baroVnavAnalysis.results) {
      if (!result.categoryResults) continue;

      const airportName = result.input.airport_name || result.input.airport_code;
      const runway = result.input.runway_designation;

      for (const cat of result.categoryResults) {
        if (!cat.obstacleRows || cat.obstacleRows.length === 0) continue;

        const matchingObs = obstacleNameFilter
          ? cat.obstacleRows.filter(o => o.obstacleName === obstacleNameFilter)
          : cat.obstacleRows;

        if (matchingObs.length === 0) continue;

        const key = `${airportName}||${runway}`;
        const hasCritical = matchingObs.some(o => o.isCritical);
        grouped.set(key, {
          airportName, runway,
          isCritical: (grouped.get(key)?.isCritical || hasCritical),
        });
      }
    }

    const comments = [];
    for (const entry of grouped.values()) {
      let impactStatus = entry.isCritical
        ? (t ? t("omniImpact") : "impact")
        : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
      if (entry.isCritical) {
        impactStatus = `~~IMPACT~~${impactStatus}~~IMPACTEND~~`;
      }
      comments.push([
        entry.airportName,
        `RWY ${entry.runway}`,
        'BARO VNAV',
        impactStatus,
      ].join(', '));
    }

    return comments;
  }

  /**
   * Builds shielding obstacle comments for ICAO Doc 015 regulation.
   * Shows one entry per nav aid (deduplicated by nav label) with worst-case impact status.
   * Impact = obstacle within protected radius (withinRadius = true).
   * @param {Object|null} shieldingAnalysis - Shielding analysis results
   * @param {Function|null} t - Translation function
   * @param {string|null} obstacleNameFilter - Optional obstacle name to filter by
   * @returns {Array} Array of formatted shielding comment strings
   */
  function buildShieldingComments(shieldingAnalysis, t, obstacleNameFilter = null) {
    if (!shieldingAnalysis || !shieldingAnalysis.results || shieldingAnalysis.results.length === 0) {
      return [];
    }

    // Group by navLabel → worst-case withinRadius across all rows for this obstacle
    const grouped = new Map();

    for (const r of shieldingAnalysis.results) {
      if (!r.rows) continue;
      // Only include nav aids that appear in the result table, i.e. have at least one
      // obstacle within the protected radius (mirrors the result-table filter).
      if (!r.rowsWithinRadius || r.rowsWithinRadius.length === 0) continue;

      const obstRows = obstacleNameFilter
        ? r.rows.filter(o => o.obstacleName === obstacleNameFilter)
        : r.rows;

      if (obstRows.length === 0) continue;

      const navLabel = r.input
        ? [r.input.airport_code, r.input.nav_ident || r.input.nav_aid_type].filter(Boolean).join(' ')
        : 'Shielding';

      const hasImpact = obstRows.some(o => o.isCritical);
      grouped.set(navLabel, {
        navLabel,
        hasImpact: (grouped.get(navLabel)?.hasImpact || hasImpact),
      });
    }

    const comments = [];
    for (const entry of grouped.values()) {
      let impactStatus = entry.hasImpact
        ? (t ? t("omniImpact") : "impact")
        : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
      if (entry.hasImpact) {
        impactStatus = `~~IMPACT~~${impactStatus}~~IMPACTEND~~`;
      }
      comments.push(`${entry.navLabel}: ${impactStatus}`);
    }

    return comments;
  }

  /**
   * Builds VSS obstacle comments for PANS OPS regulation.
   * Shows one entry per (airport, runway) with worst-case impact status.
   * Impact = critical AND NOT disregardable (for VSS rows); critical (for OCS rows).
   * @param {Object|null} vssAnalysis - VSS analysis results
   * @param {Function|null} t - Translation function
   * @param {string|null} obstacleNameFilter - Optional obstacle name to filter by
   * @returns {Array} Array of formatted VSS comment strings
   */
  function buildVssComments(vssAnalysis, t, obstacleNameFilter = null) {
    if (!vssAnalysis || !vssAnalysis.results || vssAnalysis.results.length === 0) {
      return [];
    }

    // Group by (airportName, runway) → worst-case impact
    const grouped = new Map();

    for (const result of vssAnalysis.results) {
      const airportName = result.input.airport_name || result.input.airport_code;
      const runway = result.input.runway_designation;

      const allRows = [
        ...(result.vssRows || []),
        ...(result.ocsRows || []),
      ];

      const matchingObs = obstacleNameFilter
        ? allRows.filter(o => o.obstacleName === obstacleNameFilter)
        : allRows;

      if (matchingObs.length === 0) continue;

      const key = `${airportName}||${runway}`;
      // VSS: impact = critical AND NOT disregardable; OCS: impact = critical
      const hasCriticalImpact = matchingObs.some(o =>
        o.isCritical && (o.surface === 'OCS' || !o.isDisregardable)
      );
      grouped.set(key, {
        airportName, runway,
        isCritical: (grouped.get(key)?.isCritical || hasCriticalImpact),
      });
    }

    const comments = [];
    for (const entry of grouped.values()) {
      let impactStatus = entry.isCritical
        ? (t ? t("omniImpact") : "impact")
        : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
      if (entry.isCritical) {
        impactStatus = `~~IMPACT~~${impactStatus}~~IMPACTEND~~`;
      }
      comments.push([
        entry.airportName,
        `RWY ${entry.runway}`,
        'VSS',
        impactStatus,
      ].join(', '));
    }

    return comments;
  }

  /**
   * Builds Base Turn obstacle comments for PANS OPS regulation.
   * Shows one entry per (airport, nav_type + nav_ident) with worst-case impact status.
   * @param {Object|null} holdingBaseAnalysis - Base Turn analysis results
   * @param {Function|null} t - Translation function
   * @param {string|null} obstacleNameFilter - Optional obstacle name to filter by
   * @returns {Array} Array of formatted Base Turn comment strings
   */
  function buildHoldingBaseComments(holdingBaseAnalysis, t, obstacleNameFilter = null) {
    if (!holdingBaseAnalysis || !holdingBaseAnalysis.results || holdingBaseAnalysis.results.length === 0) {
      return [];
    }

    const grouped = new Map();

    for (const result of holdingBaseAnalysis.results) {
      const airportName = result.input.airport_name || result.input.airport_code;
      const navLabel = [result.input.nav_type, result.input.nav_ident].filter(Boolean).join(' ');
      const allRows = result.obsRows || [];

      const matchingObs = obstacleNameFilter
        ? allRows.filter(o => o.obstacleName === obstacleNameFilter)
        : allRows;

      if (matchingObs.length === 0) continue;

      const key = `${airportName}||${navLabel}`;
      const hasCritical = matchingObs.some(o => o.isCritical);
      grouped.set(key, {
        airportName, navLabel,
        isCritical: (grouped.get(key)?.isCritical || hasCritical),
      });
    }

    const comments = [];
    for (const entry of grouped.values()) {
      let impactStatus = entry.isCritical
        ? (t ? t("omniImpact") : "impact")
        : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
      if (entry.isCritical) {
        impactStatus = `~~IMPACT~~${impactStatus}~~IMPACTEND~~`;
      }
      const label = entry.navLabel ? `${entry.airportName} – ${entry.navLabel}` : entry.airportName;
      comments.push([
        label,
        'Base Turn',
        impactStatus,
      ].join(', '));
    }

    return comments;
  }

  /**
   * Builds GNSS APCH obstacle comments for PANS OPS regulation.
   * Shows one entry per (airport, runway, RNAV spec, approach type) with worst-case impact status.
   * @param {Object|null} gnssApchAnalysis - GNSS APCH analysis results
   * @param {Function|null} t - Translation function
   * @param {string|null} obstacleNameFilter - Optional obstacle name to filter by
   * @returns {Array} Array of formatted GNSS APCH comment strings
   */
  function buildGnssApchComments(gnssApchAnalysis, t, obstacleNameFilter = null) {
    if (!gnssApchAnalysis || !gnssApchAnalysis.results || gnssApchAnalysis.results.length === 0) {
      return [];
    }

    const grouped = new Map();

    for (const result of gnssApchAnalysis.results) {
      if (result?.input?.error) continue;
      const segments = Array.isArray(result.segments) ? result.segments : [];
      const allRows = segments.flatMap(seg => seg?.obsRows || []);
      const matchingObs = obstacleNameFilter
        ? allRows.filter(o => o.obstacleName === obstacleNameFilter)
        : allRows;

      if (matchingObs.length === 0) continue;

      const airportCode = result.input?.airport_code || '';
      const airportName = result.input?.airport_name || '';
      const runway = result.input?.runway_designator || '';
      const rnavSpecification = result.input?.rnav_specification || '';
      const approachType = result.input?.approach_type || '';
      const hasCritical = matchingObs.some(o => !!o.isCritical);
      const key = `${airportCode}||${airportName}||${runway}||${rnavSpecification}||${approachType}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          airportCode,
          airportName,
          runway,
          rnavSpecification,
          approachType,
          isCritical: hasCritical,
        });
      } else if (hasCritical) {
        grouped.get(key).isCritical = true;
      }
    }

    const comments = [];
    for (const entry of grouped.values()) {
      let impactStatus = entry.isCritical
        ? (t ? t("omniImpact") : "impact")
        : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
      if (entry.isCritical) {
        impactStatus = `~~IMPACT~~${impactStatus}~~IMPACTEND~~`;
      }

      const airportLabel = [entry.airportCode, entry.airportName].filter(Boolean).join(' ');
      const rnavLabel = entry.rnavSpecification
        ? (entry.rnavSpecification.toUpperCase().includes('APCH')
            ? entry.rnavSpecification
            : `${entry.rnavSpecification} APCH`)
        : 'GNSS APCH';
      const commentPrefixParts = [
        airportLabel,
        entry.runway ? `RWY ${entry.runway}` : '',
        rnavLabel,
        entry.approachType || '',
      ].filter(Boolean);
      comments.push(`${commentPrefixParts.join(', ')} ${impactStatus}`.trim());
    }

    return comments;
  }

  /**
   * Builds a descriptive comment based on analysis results and regulation type.
   *
   * @param {Object} rowdef - Analysis definition with regulation type
   * @param {Array} matches - Array of matching analysis results
   * @param {Function|null} t - Translation function
   * @param {Object|null} omniAnalysis - OMNI analysis results (optional)
   * @param {string|null} obstacleNameFilter - Optional obstacle name to filter OMNI results by
   * @param {Object|null} shieldingAnalysis - Shielding analysis results (optional)
   * @returns {string} Formatted comment describing the analysis result
   */
  function buildComment(rowdef, matches, t, omniAnalysis = null, obstacleNameFilter = null, shieldingAnalysis = null) {
  if (["ANNEX 14", "EASA", "etod", "PANS OPS", "ICAO Doc 015"].includes(rowdef.regulation)) {
    // Deduplicate matches by (airport_name, surface_name, runway).
    // For lines/polygons the same surface can appear twice in the matches array: once with
    // penetrating points and once with inside-but-not-penetrating points. We keep the
    // worst-case entry per surface so the comment shows one line per surface.
    const surfaceMap = new Map();
    for (const m of matches) {
      const key = `${m.airport_name}||${m.surface_name}||${m.runway}`;
      const penetrates = m.penetrations && m.penetrations.some(p => p.penetrates);
      if (!surfaceMap.has(key) || penetrates) {
        surfaceMap.set(key, penetrates);
      }
    }

    // Build regular comments
    const regularComments = surfaceMap.size === 0
      ? []
      : [...surfaceMap.entries()].map(([key, penetrates]) => {
          const [airportName, surfaceName, runway] = key.split("||");
          return [
            airportName,
            runway ? `RWY ${runway}` : "",
            surfaceName || "",
            penetrates
              ? (t ? t("commentPenetrates") : "Objektet penetrerar ytan.")
              : (t ? t("commentWithinNoImpact") : "Objekt inom ytan, ingen påverkan.")
          ].filter(Boolean).join(", ");
        });
    
    // Add OMNI comments for PANS OPS
    if (rowdef.regulation === "PANS OPS" && omniAnalysis) {
      const omniComments = buildOmniComments(omniAnalysis, t, obstacleNameFilter);
      regularComments.push(...omniComments);
    }
    
    // Add OAS comments for PANS OPS
    if (rowdef.regulation === "PANS OPS" && oasAnalysis) {
      const oasComments = buildOasComments(oasAnalysis, t, obstacleNameFilter);
      regularComments.push(...oasComments);
    }

    // Add BARO VNAV comments for PANS OPS
    if (rowdef.regulation === "PANS OPS" && baroVnavAnalysis) {
      const baroComments = buildBaroVnavComments(baroVnavAnalysis, t, obstacleNameFilter);
      regularComments.push(...baroComments);
    }

    // Add VSS comments for PANS OPS
    if (rowdef.regulation === "PANS OPS" && vssAnalysis) {
      const vssComments = buildVssComments(vssAnalysis, t, obstacleNameFilter);
      regularComments.push(...vssComments);
    }

    // Add Base Turn comments for PANS OPS
    if (rowdef.regulation === "PANS OPS" && holdingBaseAnalysis) {
      const hbComments = buildHoldingBaseComments(holdingBaseAnalysis, t, obstacleNameFilter);
      regularComments.push(...hbComments);
    }

    // Add Holding/Racetrack comments for PANS OPS
    if (rowdef.regulation === "PANS OPS" && holdingRacetrackAnalysis && holdingRacetrackAnalysis.results) {
      for (const result of holdingRacetrackAnalysis.results) {
        const airportName = result.input.airport_name || result.input.airport_code;
        const navLabel = [result.input.nav_type, result.input.nav_ident].filter(Boolean).join(' ');
        const usedFor = result.input.used_for_type || 'Holding';
        const isByDist = result.input.limiting_dme_nm != null;
        const allRows = result.obsRows || [];
        const matchingObs = obstacleNameFilter
          ? allRows.filter(o => o.obstacleName === obstacleNameFilter)
          : allRows;
        if (matchingObs.length === 0) continue;
        const hasCritical = matchingObs.some(o => o.isCritical);
        let status = hasCritical
          ? (t ? t("omniImpact") : "impact")
          : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
        if (hasCritical) status = `~~IMPACT~~${status}~~IMPACTEND~~`;
        const label = isByDist ? `${usedFor} by Distance` : `${usedFor} by Time`;
        regularComments.push(`${label} (${airportName} ${navLabel}): ${status}`);
      }
    }

    // Add RNAV Holding comments for PANS OPS
    if (rowdef.regulation === "PANS OPS" && rnavHoldingAnalysis && rnavHoldingAnalysis.results) {
      for (const result of rnavHoldingAnalysis.results) {
        const airportName = result.input.airport_name || result.input.airport_code;
        const wptIdent = result.input.wpt_ident || '';
        const allRows = result.obsRows || [];
        const matchingObs = obstacleNameFilter
          ? allRows.filter(o => o.obstacleName === obstacleNameFilter)
          : allRows;
        if (matchingObs.length === 0) continue;
        const hasCritical = matchingObs.some(o => o.isCritical);
        let status = hasCritical
          ? (t ? t("omniImpact") : "impact")
          : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
        if (hasCritical) status = `~~IMPACT~~${status}~~IMPACTEND~~`;
        regularComments.push(`RNAV Holding (${airportName} ${wptIdent}): ${status}`);
      }
    }

    // Add RNP Holding comments for PANS OPS
    if (rowdef.regulation === "PANS OPS" && rnpHoldingAnalysis && rnpHoldingAnalysis.results) {
      for (const result of rnpHoldingAnalysis.results) {
        const airportName = result.input.airport_name || result.input.airport_code;
        const wptIdent = result.input.wpt_ident || '';
        const rnpValue = result.input.rnp_value || 1.0;
        const allRows = result.obsRows || [];
        const matchingObs = obstacleNameFilter
          ? allRows.filter(o => o.obstacleName === obstacleNameFilter)
          : allRows;
        if (matchingObs.length === 0) continue;
        const hasCritical = matchingObs.some(o => o.isCritical);
        let status = hasCritical
          ? (t ? t("omniImpact") : "impact")
          : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
        if (hasCritical) status = `~~IMPACT~~${status}~~IMPACTEND~~`;
        regularComments.push(`RNP ${rnpValue} Holding (${airportName} ${wptIdent}): ${status}`);
      }
    }

    // Add Shielding comments for ICAO Doc 015
    if (rowdef.regulation === "ICAO Doc 015" && shieldingAnalysis) {
      const shieldingComments = buildShieldingComments(shieldingAnalysis, t, obstacleNameFilter);
      regularComments.push(...shieldingComments);
    }

    // Add MSA comments for PANS OPS – grouped by nav aid (msa_id + nav_aid_name + airport)
    if (rowdef.regulation === "PANS OPS" && msaAnalysis && msaAnalysis.results) {
      const msaGrouped = new Map();
      for (const result of msaAnalysis.results) {
        if (result.input.error) continue;
        const airportName = result.input.airport_name || result.input.airport_code || '';
        const msaId = result.input.msa_id || '';
        const navAidName = result.input.nav_aid_name || '';
        const key = `${msaId}||${navAidName}||${airportName}`;
        const allRows = result.obsRows || [];
        const matchingObs = obstacleNameFilter
          ? allRows.filter(o => o.obstacleName === obstacleNameFilter)
          : allRows;
        const hasObs = matchingObs.length > 0;
        const hasCritical = matchingObs.some(o => o.isCritical);
        if (!msaGrouped.has(key)) {
          msaGrouped.set(key, { msaId, navAidName, airportName, hasObs: false, hasCritical: false });
        }
        const entry = msaGrouped.get(key);
        if (hasObs) entry.hasObs = true;
        if (hasCritical) entry.hasCritical = true;
      }
      for (const entry of msaGrouped.values()) {
        if (!entry.hasObs) continue;
        const label = `MSA ${[entry.navAidName, entry.msaId].filter(Boolean).join(' ')} (${entry.airportName})`;
        let status = entry.hasCritical
          ? (t ? t("omniImpact") : "impact")
          : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
        if (entry.hasCritical) status = `~~IMPACT~~${status}~~IMPACTEND~~`;
        regularComments.push(`${label}: ${status}`);
      }
    }

    // Add TAA comments for PANS OPS – grouped by sector_type + center_wpt + airport
    if (rowdef.regulation === "PANS OPS" && taaAnalysis && taaAnalysis.results) {
      const taaGrouped = new Map();
      for (const result of taaAnalysis.results) {
        if (result.input.error) continue;
        const airportName = result.input.airport_name || result.input.airport_code || '';
        const procedureId = result.input.procedure_id || result.input.sector_type || '';
        const key = `${procedureId}||${airportName}`;
        const allRows = result.obsRows || [];
        const matchingObs = obstacleNameFilter
          ? allRows.filter(o => o.obstacleName === obstacleNameFilter)
          : allRows;
        const hasObs = matchingObs.length > 0;
        const hasCritical = matchingObs.some(o => o.isCritical);
        if (!taaGrouped.has(key)) {
          taaGrouped.set(key, { procedureId, airportName, hasObs: false, hasCritical: false });
        }
        const entry = taaGrouped.get(key);
        if (hasObs) entry.hasObs = true;
        if (hasCritical) entry.hasCritical = true;
      }
      for (const entry of taaGrouped.values()) {
        if (!entry.hasObs) continue;
        const label = `TAA ${entry.procedureId}${entry.airportName ? ` (${entry.airportName})` : ''}`;
        let status = entry.hasCritical
          ? (t ? t("omniImpact") : "impact")
          : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
        if (entry.hasCritical) status = `~~IMPACT~~${status}~~IMPACTEND~~`;
        regularComments.push(`${label}: ${status}`);
      }
    }

    // Add Circling comments for PANS OPS – grouped by procedure + runway + airport
    if (rowdef.regulation === "PANS OPS" && circlingAnalysis && circlingAnalysis.results) {
      const circGrouped = new Map();
      for (const result of circlingAnalysis.results) {
        if (result.input.error) continue;
        const airportCode  = result.input.airport_code || '';
        const procedureId  = result.input.procedure_id || result.input.runway_designation || '';
        const key          = `${procedureId}||${airportCode}`;
        const allRows      = result.obsRows || [];
        const matchingObs  = obstacleNameFilter
          ? allRows.filter(o => o.obstacleName === obstacleNameFilter)
          : allRows;
        const hasObs      = matchingObs.length > 0;
        const hasCritical = matchingObs.some(o => o.isCritical);
        if (!circGrouped.has(key)) {
          circGrouped.set(key, { procedureId, airportCode, hasObs: false, hasCritical: false });
        }
        const entry = circGrouped.get(key);
        if (hasObs) entry.hasObs = true;
        if (hasCritical) entry.hasCritical = true;
      }
      for (const entry of circGrouped.values()) {
        if (!entry.hasObs) continue;
        const label = `Circling ${entry.procedureId}${entry.airportCode ? ` (${entry.airportCode})` : ''}`;
        let status = entry.hasCritical
          ? (t ? t("omniImpact") : "impact")
          : `~~NOIMPACT~~${t ? t("omniNoImpact") : "no impact"}~~NOIMPACTEND~~`;
        if (entry.hasCritical) status = `~~IMPACT~~${status}~~IMPACTEND~~`;
        regularComments.push(`${label}: ${status}`);
      }
    }

    if (rowdef.regulation === 'PANS OPS') {
      for (const result of locAnalysis?.results || []) {
        const hits = result.segments.flatMap(s => s.obsRows || []).filter(o => !obstacleNameFilter || o.obstacleName === obstacleNameFilter);
        if (!hits.length) continue;
        const critical = hits.some(o => o.isCritical);
        const status = critical ? `~~IMPACT~~${t ? t('omniImpact') : 'påverkan'}~~IMPACTEND~~`
          : `~~NOIMPACT~~${t ? t('omniNoImpact') : 'ingen påverkan'}~~NOIMPACTEND~~`;
        regularComments.push(`LOC ${result.input.approach_id}, ${result.input.airport_code} RWY ${result.input.runway_designator}, Cat ${result.input.aircraft_category}: ${status}`);
      }
    }

    // Add GNSS APCH comments for PANS OPS
    if (rowdef.regulation === "PANS OPS" && gnssApchAnalysis) {
      const gnssComments = buildGnssApchComments(gnssApchAnalysis, t, obstacleNameFilter);
      regularComments.push(...gnssComments);
    }

    // Return combined comments
    if (regularComments.length === 0) {
      return t ? t("commentNoImpact") : "Ingen påverkan.";
    }
    return regularComments.join("; ");
  }
    if (rowdef.regulation === "TMA_TIA") {
      if (matches.length === 0) return t ? t("commentOutsideTmaTia") : "Objekt utanför TMA/TIA-luftrum.";
      return matches
        .map(m =>
          [
            m.name_and_limits,
            m.airspace_type ? `(${m.airspace_type})` : "",
            m.service_unit ? `${t ? t("unit") : "Enhet"}: ${m.service_unit}` : "",
            m.airspace_type
              ? (t ? t("commentWithinAirspace").replace("{type}", m.airspace_type) : `Objektet inom ${m.airspace_type}-luftrum.`)
              : (t ? t("commentWithinTmaTia") : "Objektet inom TMA/TIA-luftrum.")
          ].filter(Boolean).join(", ")
        )
        .join("; ");
    }
    if (rowdef.regulation === "ATS") {
      if (matches.length === 0) return t ? t("commentOutsideAts") : "Objekt utanför kontrollerat ATS-luftrum.";
      return matches
        .map(m =>
          [
            m.airspace_name,
            m.airport_code ? m.airport_code : "",
            m.lower_limit && m.upper_limit ? `(${m.lower_limit} - ${m.upper_limit})` : "",
            m.airspace_type
              ? (t ? t("commentWithinAirspace").replace("{type}", m.airspace_type) : `Objektet inom ${m.airspace_type}-luftrum.`)
              : (t ? t("commentWithinAts") : "Objektet inom ATS-luftrum.")
          ].filter(Boolean).join(", ")
        )
        .join("; ");
    }
    return "";
  }

  /**
   * Creates an optimized index of matches grouped by regulation and airspace type patterns.
   * This eliminates the need to filter through all matches repeatedly for each analysis type.
   * 
   * @param {Array} allMatches - All matches from the result group
   * @returns {Object} Index object with pre-categorized matches
   */
  function createMatchIndex(allMatches) {
    const index = {
      byRegulation: {
        "ANNEX 14": [],
        "EASA": [],
        "etod": [],
        "PANS OPS": [],
        "ICAO Doc 015": []
      },
      tmaTia: [],
      ats: []
    };

    // Pre-categorize all matches once instead of filtering repeatedly
    allMatches.forEach(match => {
      // Index by regulation (case-insensitive)
      const regulation = (match.regulation || "").toUpperCase();
      if (index.byRegulation[regulation]) {
        index.byRegulation[regulation].push(match);
      }
      if (regulation === "ETOD") {
        index.byRegulation["etod"].push(match);
      }
      // Handle both old and new ICAO regulation names for backwards compatibility
      if (regulation === "ICAO DOC 013" || regulation === "ICAO DOC 015") {
        index.byRegulation["ICAO Doc 015"].push(match);
      }

      // Index by airspace type patterns
      if (typeof match.airspace_type === "string") {
        const airspaceType = match.airspace_type.toUpperCase();
        
        if (airspaceType.includes("TMA") || airspaceType.includes("TIA")) {
          index.tmaTia.push(match);
        }
        
        if (airspaceType.includes("ATS") || airspaceType.includes("CTR") || 
            airspaceType.includes("TIZ") || airspaceType.includes("RMZ")) {
          index.ats.push(match);
        }
      }
    });

    return index;
  }

  /**
   * Retrieves matches from the pre-built index for a given analysis definition.
   * This replaces the original filter operation for performance.
   * 
   * @param {Object} matchIndex - Pre-built index from createMatchIndex
   * @param {Object} rowdef - Analysis definition with regulation and matchFn
   * @returns {Array} Array of matching items
   */
  function getMatchesFromIndex(matchIndex, rowdef) {
    // Use indexed lookup for performance instead of filtering
    switch (rowdef.regulation) {
      case "ANNEX 14":
      case "EASA":
      case "etod":
      case "PANS OPS":
      case "ICAO Doc 015":
        return matchIndex.byRegulation[rowdef.regulation] || [];
      case "TMA_TIA":
        return matchIndex.tmaTia;
      case "ATS":
        return matchIndex.ats;
      default:
        // Fallback to original filtering for unknown regulations
        return [];
    }
  }

  // -- Sammanställning --
  const rows = [];
  if (!analysis) return rows;
  // Grupp per objekt/cirkel
  const grouped = {};
  analysis.forEach((result, idx) => {
    const circleId = result?.properties?.circleId !== undefined
      ? `circle-${result.properties.circleId}`
      : `objekt-${result?.properties?.id ?? idx}`;
    if (!grouped[circleId]) grouped[circleId] = [];
    grouped[circleId].push(result);
  });
  Object.entries(grouped).forEach(([circleId, resultGroup], groupIdx) => {
    const objektnamn = getObjektnamn(resultGroup[0].properties, groupIdx);
    const allMatches = resultGroup.flatMap(r => r.matches || []);
    
    // Performance optimization: Create index of matches by type to avoid repeated filtering
    const matchIndex = createMatchIndex(allMatches);
    
    ANALYS_DEFS.forEach(rowdef => {
      // Use indexed lookup instead of filtering for performance
      const matches = getMatchesFromIndex(matchIndex, rowdef);
      
      // For PANS OPS, check if we have OMNI results for this specific obstacle
      let hasOmniObstacles = false;
      if (rowdef.regulation === "PANS OPS" && omniAnalysis && omniAnalysis.results) {
        hasOmniObstacles = omniAnalysis.results.some(r => 
          r.obstacles && r.obstacles.some(o => o.obstacleName === objektnamn)
        );
      }

      // For PANS OPS, check if we have OAS results for this specific obstacle
      let hasOasObstacles = false;
      if (rowdef.regulation === "PANS OPS" && oasAnalysis && oasAnalysis.results) {
        hasOasObstacles = oasAnalysis.results.some(r =>
          r.categoryResults && r.categoryResults.some(c =>
            c.obstacles && c.obstacles.some(o => o.obstacleName === objektnamn)
          )
        );
      }

      // For PANS OPS, check if we have BARO VNAV results for this specific obstacle
      let hasBaroObstacles = false;
      if (rowdef.regulation === "PANS OPS" && baroVnavAnalysis && baroVnavAnalysis.results) {
        hasBaroObstacles = baroVnavAnalysis.results.some(r =>
          r.categoryResults && r.categoryResults.some(c =>
            c.obstacleRows && c.obstacleRows.some(o => o.obstacleName === objektnamn)
          )
        );
      }

      // For PANS OPS, check if we have VSS results for this specific obstacle
      let hasVssObstacles = false;
      if (rowdef.regulation === "PANS OPS" && vssAnalysis && vssAnalysis.results) {
        hasVssObstacles = vssAnalysis.results.some(r =>
          (r.vssRows && r.vssRows.some(o => o.obstacleName === objektnamn)) ||
          (r.ocsRows && r.ocsRows.some(o => o.obstacleName === objektnamn))
        );
      }

      // For ICAO Doc 015, check if shielding has results for this obstacle
      let hasShieldingObstacles = false;
      if (rowdef.regulation === "ICAO Doc 015" && shieldingAnalysis && shieldingAnalysis.results) {
        hasShieldingObstacles = shieldingAnalysis.results.some(r =>
          r.rows && r.rows.some(o => o.obstacleName === objektnamn)
        );
      }

      // For PANS OPS, check if we have Base Turn (holding base) results for this specific obstacle
      let hasHoldingBaseObstaclesEarly = false;
      if (rowdef.regulation === "PANS OPS" && holdingBaseAnalysis && holdingBaseAnalysis.results) {
        hasHoldingBaseObstaclesEarly = holdingBaseAnalysis.results.some(r =>
          r.obsRows && r.obsRows.some(o => o.obstacleName === objektnamn)
        );
      }

      // For PANS OPS, check if we have Holding/Racetrack results for this specific obstacle
      let hasHoldingRacetrackObstaclesEarly = false;
      if (rowdef.regulation === "PANS OPS" && holdingRacetrackAnalysis && holdingRacetrackAnalysis.results) {
        hasHoldingRacetrackObstaclesEarly = holdingRacetrackAnalysis.results.some(r =>
          r.obsRows && r.obsRows.some(o => o.obstacleName === objektnamn)
        );
      }

      // For PANS OPS, check if we have RNAV Holding results for this specific obstacle
      let hasRnavHoldingObstaclesEarly = false;
      if (rowdef.regulation === "PANS OPS" && rnavHoldingAnalysis && rnavHoldingAnalysis.results) {
        hasRnavHoldingObstaclesEarly = rnavHoldingAnalysis.results.some(r =>
          r.obsRows && r.obsRows.some(o => o.obstacleName === objektnamn)
        );
      }

      // For PANS OPS, check if we have RNP Holding results for this specific obstacle
      let hasRnpHoldingObstaclesEarly = false;
      if (rowdef.regulation === "PANS OPS" && rnpHoldingAnalysis && rnpHoldingAnalysis.results) {
        hasRnpHoldingObstaclesEarly = rnpHoldingAnalysis.results.some(r =>
          r.obsRows && r.obsRows.some(o => o.obstacleName === objektnamn)
        );
      }

      // For PANS OPS, check if we have GNSS APCH results for this specific obstacle
      let hasGnssApchObstaclesEarly = false;
      if (rowdef.regulation === "PANS OPS" && gnssApchAnalysis && gnssApchAnalysis.results) {
        hasGnssApchObstaclesEarly = gnssApchAnalysis.results.some(r =>
          (r.segments || []).some(seg =>
            seg.obsRows && seg.obsRows.some(o => o.obstacleName === objektnamn)
          )
        );
      }
      
      const locHits = rowdef.regulation === 'PANS OPS'
        ? (locAnalysis?.results || []).flatMap(r => r.segments.flatMap(s => s.obsRows || [])).filter(o => o.obstacleName === objektnamn) : [];
      const hasLocObstaclesEarly = locHits.length > 0;
      const locHasCritical = locHits.some(o => o.isCritical);
      const inom = hasLocObstaclesEarly || matches.length > 0 || (rowdef.regulation === "PANS OPS" && (hasOmniObstacles || hasOasObstacles || hasBaroObstacles || hasVssObstacles || hasHoldingBaseObstaclesEarly || hasHoldingRacetrackObstaclesEarly || hasRnavHoldingObstaclesEarly || hasRnpHoldingObstaclesEarly || hasGnssApchObstaclesEarly)) || (rowdef.regulation === "ICAO Doc 015" && hasShieldingObstacles);
      const penetrerade =
        ["ANNEX 14", "EASA", "etod", "PANS OPS", "ICAO Doc 015"].includes(rowdef.regulation)
          ? matches.some(m =>
              m.penetrations && m.penetrations.some(p => p.penetrates)
            )
          : false;
      
      // For PANS OPS, check if any OMNI obstacles matching current object are critical (not close-in)
      let omniHasCritical = false;
      if (rowdef.regulation === "PANS OPS" && omniAnalysis && omniAnalysis.results) {
        omniHasCritical = omniAnalysis.results.some(r => 
          r.obstacles && r.obstacles.some(o => 
            o.obstacleName === objektnamn && o.isCritical && !o.isCloseIn
          )
        );
      }

      // For PANS OPS, check if any OAS obstacles matching current object are critical
      let oasHasCritical = false;
      if (rowdef.regulation === "PANS OPS" && oasAnalysis && oasAnalysis.results) {
        oasHasCritical = oasAnalysis.results.some(r =>
          r.categoryResults && r.categoryResults.some(c =>
            c.obstacles && c.obstacles.some(o =>
              o.obstacleName === objektnamn && o.isCritical
            )
          )
        );
      }

      // For PANS OPS, check if any BARO VNAV obstacles matching current object are critical
      let baroHasCritical = false;
      if (rowdef.regulation === "PANS OPS" && baroVnavAnalysis && baroVnavAnalysis.results) {
        baroHasCritical = baroVnavAnalysis.results.some(r =>
          r.categoryResults && r.categoryResults.some(c =>
            c.obstacleRows && c.obstacleRows.some(o =>
              o.obstacleName === objektnamn && o.isCritical
            )
          )
        );
      }

      // For PANS OPS, check if any VSS obstacles have impact (critical AND not disregardable for VSS; critical for OCS)
      let vssHasCritical = false;
      if (rowdef.regulation === "PANS OPS" && vssAnalysis && vssAnalysis.results) {
        vssHasCritical = vssAnalysis.results.some(r =>
          [...(r.vssRows || []), ...(r.ocsRows || [])].some(o =>
            o.obstacleName === objektnamn && o.isCritical &&
            (o.surface === 'OCS' || !o.isDisregardable)
          )
        );
      }

      // For ICAO Doc 015, check if shielding has any obstacle that is critical (penetrates above the surface)
      let shieldingHasCritical = false;
      if (rowdef.regulation === "ICAO Doc 015" && shieldingAnalysis && shieldingAnalysis.results) {
        shieldingHasCritical = shieldingAnalysis.results.some(r =>
          r.rowsWithinRadius && r.rowsWithinRadius.some(o => o.obstacleName === objektnamn && o.isCritical)
        );
      }

      // For PANS OPS, check if any Base Turn obstacles matching current object are critical
      let holdingBaseHasCritical = false;
      if (rowdef.regulation === "PANS OPS" && holdingBaseAnalysis && holdingBaseAnalysis.results) {
        holdingBaseHasCritical = holdingBaseAnalysis.results.some(r =>
          r.obsRows && r.obsRows.some(o => o.obstacleName === objektnamn && o.isCritical)
        );
      }

      // For PANS OPS, check if any Holding/Racetrack obstacles are critical
      let holdingRacetrackHasCritical = false;
      if (rowdef.regulation === "PANS OPS" && holdingRacetrackAnalysis && holdingRacetrackAnalysis.results) {
        holdingRacetrackHasCritical = holdingRacetrackAnalysis.results.some(r =>
          r.obsRows && r.obsRows.some(o => o.obstacleName === objektnamn && o.isCritical)
        );
      }

      // For PANS OPS, check if any RNAV Holding obstacles are critical
      let rnavHoldingHasCritical = false;
      if (rowdef.regulation === "PANS OPS" && rnavHoldingAnalysis && rnavHoldingAnalysis.results) {
        rnavHoldingHasCritical = rnavHoldingAnalysis.results.some(r =>
          r.obsRows && r.obsRows.some(o => o.obstacleName === objektnamn && o.isCritical)
        );
      }

      // For PANS OPS, check if any RNP Holding obstacles are critical
      let rnpHoldingHasCritical = false;
      if (rowdef.regulation === "PANS OPS" && rnpHoldingAnalysis && rnpHoldingAnalysis.results) {
        rnpHoldingHasCritical = rnpHoldingAnalysis.results.some(r =>
          r.obsRows && r.obsRows.some(o => o.obstacleName === objektnamn && o.isCritical)
        );
      }

      // For PANS OPS, check if any GNSS APCH obstacles are critical
      let gnssApchHasCritical = false;
      if (rowdef.regulation === "PANS OPS" && gnssApchAnalysis && gnssApchAnalysis.results) {
        gnssApchHasCritical = gnssApchAnalysis.results.some(r =>
          (r.segments || []).some(seg =>
            seg.obsRows && seg.obsRows.some(o => o.obstacleName === objektnamn && o.isCritical)
          )
        );
      }

      const utanAnm = inom && !penetrerade && !omniHasCritical && !oasHasCritical && !baroHasCritical && !vssHasCritical && !holdingBaseHasCritical && !holdingRacetrackHasCritical && !rnavHoldingHasCritical && !rnpHoldingHasCritical && !gnssApchHasCritical && !locHasCritical && !shieldingHasCritical;
      const anmarkning = penetrerade || omniHasCritical || oasHasCritical || baroHasCritical || vssHasCritical || holdingBaseHasCritical || holdingRacetrackHasCritical || rnavHoldingHasCritical || rnpHoldingHasCritical || gnssApchHasCritical || locHasCritical || shieldingHasCritical;
      const kommentar = buildComment(rowdef, matches, t, omniAnalysis, objektnamn, shieldingAnalysis);
      const cleanKommentar = mode === "pdf" ? stripPenetrationStylingForPDF(kommentar) : kommentar;
       // NY LOGIK FÖR PDF/HTML
      rows.push([
        objektnamn,
        rowdef.typ,
        inom ? "JA" : "",
        !inom ? "NEJ" : "",
        cleanKommentar,
        utanAnm ? "JA" : "",
        anmarkning ? "NEJ" : "",
      ]);
    });
  });
  return rows;
}

// Attach circleToPolygon as a property to the function
buildSummaryRowsFromAnalysis.circleToPolygon = circleToPolygon;